import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, ComposedChart, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area } from 'recharts';
import api from '../services/api';
import ObservationAnalysisDashboard from './ObservationAnalysisDashboard';

const AnalyticsExamples = ({ patientId, patientName }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [obsAnalysis, setObsAnalysis] = useState('');
  const [obsAnalysisLoading, setObsAnalysisLoading] = useState(false);
  const [obsAnalysisError, setObsAnalysisError] = useState(null);
  
  // Sample data for different analytics views
  const programProgressData = [
    { month: 'Jan', current: 25, target: 80 },
    { month: 'Feb', current: 32, target: 80 },
    { month: 'Mar', current: 40, target: 80 },
    { month: 'Apr', current: 45, target: 80 },
    { month: 'May', current: 58, target: 80 },
    { month: 'Jun', current: 70, target: 80 },
  ];
  
  const behaviorsData = [
    { date: '6/1', selfInjury: 8, aggression: 5, tantrums: 10 },
    { date: '6/2', selfInjury: 6, aggression: 4, tantrums: 11 },
    { date: '6/3', selfInjury: 7, aggression: 6, tantrums: 8 },
    { date: '6/4', selfInjury: 5, aggression: 3, tantrums: 7 },
    { date: '6/5', selfInjury: 4, aggression: 5, tantrums: 6 },
    { date: '6/6', selfInjury: 5, aggression: 2, tantrums: 5 },
    { date: '6/7', selfInjury: 3, aggression: 1, tantrums: 4 },
  ];
  
  const promptingData = [
    { name: 'Independent', value: 45 },
    { name: 'Verbal Prompt', value: 25 },
    { name: 'Gestural Prompt', value: 15 },
    { name: 'Model Prompt', value: 10 },
    { name: 'Physical Prompt', value: 5 },
  ];
  
  const biometricCorrelationData = [
    { time: '9:00', heartRate: 85, behaviors: 0, therapy: 'Baseline' },
    { time: '9:30', heartRate: 100, behaviors: 2, therapy: 'Demand' },
    { time: '10:00', heartRate: 110, behaviors: 4, therapy: 'Demand' },
    { time: '10:30', heartRate: 90, behaviors: 1, therapy: 'Break' },
    { time: '11:00', heartRate: 95, behaviors: 0, therapy: 'Preferred Activity' },
    { time: '11:30', heartRate: 105, behaviors: 3, therapy: 'Demand' },
    { time: '12:00', heartRate: 85, behaviors: 0, therapy: 'Lunch' },
  ];
  
  const targetMasteryData = [
    { target: 'Requesting', mastered: 85, inProgress: 15 },
    { target: 'Labeling', mastered: 70, inProgress: 30 },
    { target: 'Following Instructions', mastered: 60, inProgress: 40 },
    { target: 'Waiting', mastered: 50, inProgress: 50 },
    { target: 'Sharing', mastered: 40, inProgress: 60 },
    { target: 'Transitions', mastered: 30, inProgress: 70 },
  ];
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const tabTitles = [
    'Progresso do Programa',
    'Acompanhamento de Comportamentos',
    'Análise de Suporte',
    'Correlação com Frequência Cardíaca',
    'Domínio de Objetivos',
    'Análise Observação'
  ];

  useEffect(() => {
    if (activeTab === 5 && patientId) {
      setObsAnalysisLoading(true);
      setObsAnalysisError(null);
      api.get(`/patients/${patientId}/observation-analysis/latest`)
        .then(res => setObsAnalysis(res.data.dashboard))
        .catch(err => setObsAnalysisError(err.response?.data?.error || err.message))
        .finally(() => setObsAnalysisLoading(false));
    }
  }, [activeTab, patientId]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6">Análise Avançada para Terapia ABA</h2>
      
      <div className="mb-6">
        <ul className="flex border-b">
          {tabTitles.map((tab, index) => (
            <li key={index} className="mr-1">
              <button
                className={`py-2 px-4 font-medium rounded-t-lg ${activeTab === index 
                  ? 'bg-brand-700 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                onClick={() => setActiveTab(index)}
              >
                {tab}
              </button>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="panel-container">
        {/* Program Progress Panel */}
        {activeTab === 0 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Progresso do Programa do Aluno ao Longo do Tempo</h3>
              <div className="text-sm text-gray-500">Mostrando progresso em direção aos critérios de domínio</div>
            </div>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={programProgressData}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} label={{ value: 'Porcentagem de Habilidade', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <defs>
                    <linearGradient id="progressGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  <Bar dataKey="current" barSize={30} fill="url(#progressGradient)" name="Nível Atual" />
                  <Line type="monotone" dataKey="target" stroke="#ff7300" strokeWidth={2} name="Critério de Domínio" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="bg-brand-50 p-4 rounded">
                <h4 className="text-brand-800 font-medium mb-2">Taxa de Aquisição de Habilidades</h4>
                <p className="text-2xl font-bold">+8,3%</p>
                <p className="text-sm text-gray-600">Melhoria média mensal</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <h4 className="text-green-800 font-medium mb-2">Data Prevista de Domínio</h4>
                <p className="text-2xl font-bold">15 de Agosto</p>
                <p className="text-sm text-gray-600">Baseado no progresso atual</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <h4 className="text-purple-800 font-medium mb-2">Pontuação de Consistência</h4>
                <p className="text-2xl font-bold">87%</p>
                <p className="text-sm text-gray-600">Estabilidade do desempenho</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Behavior Tracking Panel */}
        {activeTab === 1 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Frequência de Comportamentos Alvo</h3>
              <div className="text-sm text-gray-500">Ocorrências diárias na última semana</div>
            </div>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={behaviorsData}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis label={{ value: 'Ocorrências', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="selfInjury" stroke="#ff0000" strokeWidth={2} name="Autolesão" />
                  <Line type="monotone" dataKey="aggression" stroke="#ff7300" strokeWidth={2} name="Agressão" />
                  <Line type="monotone" dataKey="tantrums" stroke="#8884d8" strokeWidth={2} name="Birras" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="bg-red-50 p-4 rounded">
                <h4 className="text-red-800 font-medium mb-2">Redução de Autolesão</h4>
                <p className="text-2xl font-bold">-62,5%</p>
                <p className="text-sm text-gray-600">Desde o início da intervenção</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <h4 className="text-orange-800 font-medium mb-2">Redução de Agressão</h4>
                <p className="text-2xl font-bold">-80%</p>
                <p className="text-sm text-gray-600">Desde o início da intervenção</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <h4 className="text-purple-800 font-medium mb-2">Redução de Birras</h4>
                <p className="text-2xl font-bold">-60%</p>
                <p className="text-sm text-gray-600">Desde o início da intervenção</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Prompting Analysis Panel */}
        {activeTab === 2 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Distribuição de Níveis de Suporte</h3>
              <div className="text-sm text-gray-500">Em todos os programas</div>
            </div>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Independente', value: 45 },
                      { name: 'Suporte Verbal', value: 25 },
                      { name: 'Suporte Gestual', value: 15 },
                      { name: 'Suporte por Modelo', value: 10 },
                      { name: 'Suporte Físico', value: 5 }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {promptingData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="bg-brand-50 p-4 rounded">
                <h4 className="text-brand-800 font-medium mb-2">Nível de Independência</h4>
                <p className="text-2xl font-bold">45%</p>
                <p className="text-sm text-gray-600">Respostas sem suporte</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <h4 className="text-green-800 font-medium mb-2">Dependência de Suporte</h4>
                <p className="text-2xl font-bold">Baixa</p>
                <p className="text-sm text-gray-600">Baseado na distribuição de suporte</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <h4 className="text-purple-800 font-medium mb-2">Tendência de Suporte</h4>
                <p className="text-2xl font-bold">↑ Reduzindo</p>
                <p className="text-sm text-gray-600">Caminhando para independência</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Heart Rate Correlation Panel */}
        {activeTab === 3 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Correlação entre Frequência Cardíaca e Comportamento</h3>
              <div className="text-sm text-gray-500">Sessão em 10 de Junho de 2025</div>
            </div>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={biometricCorrelationData}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis yAxisId="left" orientation="left" label={{ value: 'Frequência Cardíaca (BPM)', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Ocorrências de Comportamento', angle: 90, position: 'insideRight' }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="heartRate" stroke="#ff0000" strokeWidth={2} name="Frequência Cardíaca" />
                  <Bar yAxisId="right" dataKey="behaviors" barSize={20} fill="#8884d8" name="Ocorrências de Comportamento" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="bg-red-50 p-4 rounded">
                <h4 className="text-red-800 font-medium mb-2">Coeficiente de Correlação</h4>
                <p className="text-2xl font-bold">0,78</p>
                <p className="text-sm text-gray-600">Entre FC e comportamentos</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <h4 className="text-green-800 font-medium mb-2">FC Média em Repouso</h4>
                <p className="text-2xl font-bold">85 BPM</p>
                <p className="text-sm text-gray-600">Durante períodos sem demanda</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <h4 className="text-purple-800 font-medium mb-2">Gatilho para Intervenção</h4>
                <p className="text-2xl font-bold">105+ BPM</p>
                <p className="text-sm text-gray-600">Limite recomendado para pausa</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Target Mastery Panel */}
        {activeTab === 4 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Status de Domínio dos Objetivos</h3>
              <div className="text-sm text-gray-500">Por categoria de objetivo</div>
            </div>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { target: 'Solicitação', mastered: 85, inProgress: 15 },
                    { target: 'Nomeação', mastered: 70, inProgress: 30 },
                    { target: 'Seguir Instruções', mastered: 60, inProgress: 40 },
                    { target: 'Esperar', mastered: 50, inProgress: 50 },
                    { target: 'Compartilhar', mastered: 40, inProgress: 60 },
                    { target: 'Transições', mastered: 30, inProgress: 70 }
                  ]}
                  layout="vertical"
                  margin={{ top: 20, right: 20, bottom: 20, left: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="target" type="category" width={100} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend />
                  <Bar dataKey="mastered" stackId="a" fill="#4CAF50" name="Dominado" />
                  <Bar dataKey="inProgress" stackId="a" fill="#FFC107" name="Em Progresso" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="bg-brand-50 p-4 rounded">
                <h4 className="text-brand-800 font-medium mb-2">Taxa Geral de Domínio</h4>
                <p className="text-2xl font-bold">55,8%</p>
                <p className="text-sm text-gray-600">Em todos os objetivos</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <h4 className="text-green-800 font-medium mb-2">Progresso Mais Rápido</h4>
                <p className="text-2xl font-bold">Solicitação</p>
                <p className="text-sm text-gray-600">85% dominado</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <h4 className="text-purple-800 font-medium mb-2">Área de Foco</h4>
                <p className="text-2xl font-bold">Transições</p>
                <p className="text-sm text-gray-600">Menor taxa de domínio</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Análise Observação Panel */}
        {activeTab === 5 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium mb-4">Análise das Observações do Paciente</h3>
            {obsAnalysisLoading ? (
              <div>Carregando análise...</div>
            ) : obsAnalysisError ? (
              <div className="text-red-600">{obsAnalysisError}</div>
            ) : obsAnalysis ? (
              <ObservationAnalysisDashboard dashboard={obsAnalysis} patientName={patientName} />
            ) : (
              <div className="text-gray-500">Nenhuma análise disponível.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsExamples; 